import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronLeft } from 'lucide-react';
import useParams from '../hooks/useParams';
import { apiFetch } from '../utils/api';
import WorkoutDayEditor from '../components/WorkoutDayEditor';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

const EditWorkoutPage = () => {
  const { workoutId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [days, setDays] = useState([]);

  useEffect(() => { fetchWorkout(); }, [workoutId]);

  const fetchWorkout = async () => {
    try {
      const response = await apiFetch(`/api/workouts/${workoutId}`);
      if (response.ok) {
        const data = await response.json();
        setName(data.name);
        setDescription(data.description || '');
        setDays(data.days || []);
      }
    } catch (error) {
      console.error('Error fetching workout:', error);
    } finally {
      setLoading(false);
    }
  };

  const addDay = () =>
    setDays(prev => [
      ...prev,
      { day_number: prev.length + 1, name: i18n.t('workout.day_default_name', { number: prev.length + 1 }), exercises: [] }
    ]);

  const removeDay = (dayIndex) =>
    setDays(prev => prev.filter((_, i) => i !== dayIndex));

  const updateDayName = (dayIndex, value) =>
    setDays(prev => {
      const updated = [...prev];
      updated[dayIndex] = { ...updated[dayIndex], name: value };
      return updated;
    });

  const addExercise = (dayIndex) =>
    setDays(prev => {
      const updated = [...prev];
      updated[dayIndex] = {
        ...updated[dayIndex],
        exercises: [...updated[dayIndex].exercises, { name: '', sets: '', notes: '' }]
      };
      return updated;
    });

  const updateExercise = (dayIndex, exIndex, field, value) =>
    setDays(prev => {
      const updated = [...prev];
      const exercises = [...updated[dayIndex].exercises];
      exercises[exIndex] = { ...exercises[exIndex], [field]: value };
      updated[dayIndex] = { ...updated[dayIndex], exercises };
      return updated;
    });

  const removeExercise = (dayIndex, exIndex) =>
    setDays(prev => {
      const updated = [...prev];
      updated[dayIndex] = {
        ...updated[dayIndex],
        exercises: updated[dayIndex].exercises.filter((_, i) => i !== exIndex)
      };
      return updated;
    });

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const response = await apiFetch(`/api/workouts/${workoutId}`, {
        method: 'PUT',
        body: JSON.stringify({ name, description, days })
      });
      if (response.ok) navigate(`/workout/${workoutId}`);
    } catch (error) {
      console.error('Error updating workout:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading"><div className="loading-spinner" /></div>;

  return (
    <div className="page-container animate-fade-in">
      <div className="workout-page-header">
        <button className="workout-page-back" onClick={() => navigate(-1)}>
          <ChevronLeft size={14} />
          {t('common.back')}
        </button>
        <h1 className="header-title">{t('workout.edit_routine')}</h1>
      </div>

      <div className="workout-info-card">
        <div className="input-group">
          <label className="input-label">{t('workout.routine_name_label')}</label>
          <input
            type="text"
            className="input"
            placeholder={t('workout.routine_name_placeholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="input-group" style={{ marginBottom: 0 }}>
          <label className="input-label">
            {t('workout.description_label')}{' '}
            <span style={{ color: 'var(--muted-foreground)', fontWeight: 400, textTransform: 'none' }}>
              {t('common.optional')}
            </span>
          </label>
          <input
            type="text"
            className="input"
            placeholder={t('workout.description_placeholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>

      <p className="days-section-title">{t('workout.training_days')}</p>

      {days.map((day, dayIndex) => (
        <WorkoutDayEditor
          key={dayIndex}
          day={day}
          dayIndex={dayIndex}
          totalDays={days.length}
          onUpdateName={updateDayName}
          onAddExercise={addExercise}
          onUpdateExercise={updateExercise}
          onRemoveExercise={removeExercise}
          onRemoveDay={removeDay}
        />
      ))}

      <button className="btn-add-day" onClick={addDay}>
        <Plus size={16} />
        {t('workout.add_day')}
      </button>

      <div className="save-section">
        <button
          className="btn btn-primary w-full"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving
            ? <div className="loading-spinner" style={{ width: '20px', height: '20px' }} />
            : t('workout.update_btn')
          }
        </button>
      </div>
    </div>
  );
};

export default EditWorkoutPage;
